import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { licenseApiService, type LicenseRequestPayload } from '@/services/licenseApiService'
import { useAuth } from '@/hooks/useAuth'

export interface RequestLicenseModalRef {
  open: () => void
}

type LicenseType = LicenseRequestPayload['license_type']

// The length each type is usually asked for. Matches the issuing form, so what
// a customer asks for is what the vendor sees suggested.
const DEFAULT_DAYS: Record<LicenseType, number> = {
  trial: 7,
  standard: 365,
  enterprise: 365,
}

/**
 * Asks the vendor for a license (BAFTT-12).
 *
 * Files a request rather than sending mail: the customer can follow it under
 * Platform Requests, and the vendor answers it from the issuing form with
 * these fields already filled.
 */
const RequestLicenseModal = forwardRef<RequestLicenseModalRef>((_, ref) => {
  const { t } = useTranslation(['plans', 'common'])
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  const [kind, setKind] = useState<LicenseRequestPayload['kind']>('renewal')
  const [customerName, setCustomerName] = useState('')
  const [email, setEmail] = useState('')
  const [organization, setOrganization] = useState('')
  const [licenseType, setLicenseType] = useState<LicenseType>('standard')
  const [validDays, setValidDays] = useState(DEFAULT_DAYS.standard)
  const [maxFlows, setMaxFlows] = useState('')
  const [note, setNote] = useState('')

  useImperativeHandle(ref, () => ({
    open: () => {
      setKind('renewal')
      setCustomerName(user?.name ?? '')
      setEmail(user?.account?.includes('@') ? user.account : '')
      setOrganization('')
      setLicenseType('standard')
      setValidDays(DEFAULT_DAYS.standard)
      setMaxFlows('')
      setNote('')
      setError('')
      setSent(false)
      setIsOpen(true)
    },
  }))

  const submit = async () => {
    if (isLoading) return
    if (!customerName.trim() || !email.trim()) {
      setError(t('requestLicense.errors.required', 'Name and email are required.'))
      return
    }
    setIsLoading(true)
    setError('')
    try {
      await licenseApiService.requestLicense({
        kind,
        customer_name: customerName.trim(),
        email: email.trim(),
        organization: organization.trim() || undefined,
        license_type: licenseType,
        valid_days: validDays,
        max_flows: maxFlows.trim() ? Number(maxFlows) : undefined,
        note: note.trim() || undefined,
      })
      setSent(true)
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t('issueModal.errors.generic'))
    } finally {
      setIsLoading(false)
    }
  }

  const close = () => {
    if (!isLoading) setIsOpen(false)
  }

  if (!isOpen) return null

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/30 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-primary">{t('requestLicense.heading', 'Request a license')}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('requestLicense.subtitle', 'Sent to the Synstream team for review')}
              </p>
            </div>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
              aria-label={t('common:close', 'Close')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {sent ? (
          <>
            <div className="p-6">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-900">{t('requestLicense.sent.title', 'Request sent')}</p>
                <p className="text-sm text-green-800 mt-1">
                  {t(
                    'requestLicense.sent.body',
                    'You can follow it under Platform Requests, in My Requests. Once it is issued, the key is emailed to the address you gave.'
                  )}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false)
                  navigate('/user-requests?tab=my')
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('requestLicense.sent.track', 'View my requests')}
              </button>
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                {t('common:close', 'Close')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 space-y-4">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                {(['renewal', 'new'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKind(k)}
                    disabled={isLoading}
                    className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                      kind === k
                        ? 'border-primary bg-primary/5 text-primary font-medium'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {k === 'renewal'
                      ? t('requestLicense.kind.renewal', 'Renew or extend')
                      : t('requestLicense.kind.new', 'New license')}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.customerName')} *
                  </label>
                  <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} disabled={isLoading} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.email')} *
                  </label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isLoading} className={inputClass} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('issueModal.fields.organization')}
                </label>
                <input type="text" value={organization} onChange={(e) => setOrganization(e.target.value)} disabled={isLoading} className={inputClass} />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.licenseType')}
                  </label>
                  <select
                    value={licenseType}
                    onChange={(e) => {
                      const next = e.target.value as LicenseType
                      setLicenseType(next)
                      setValidDays(DEFAULT_DAYS[next])
                    }}
                    disabled={isLoading}
                    className={inputClass}
                  >
                    <option value="trial">{t('issueModal.types.trial')}</option>
                    <option value="standard">{t('issueModal.types.standard')}</option>
                    <option value="enterprise">{t('issueModal.types.enterprise')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.validDays')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    value={validDays}
                    onChange={(e) => setValidDays(Number(e.target.value))}
                    disabled={isLoading}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('issueModal.fields.maxFlows')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={maxFlows}
                    onChange={(e) => setMaxFlows(e.target.value)}
                    disabled={isLoading}
                    placeholder={t('requestLicense.flowsHint', 'Default')}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('requestLicense.note', 'Note for the Synstream team')}
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={isLoading}
                  rows={3}
                  maxLength={2000}
                  placeholder={t('requestLicense.notePlaceholder', 'Contract number, what changed, anything that helps')}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isLoading}
              >
                {t('common:cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={isLoading}
              >
                {isLoading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{t('requestLicense.submit', 'Send request')}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
})

RequestLicenseModal.displayName = 'RequestLicenseModal'
export default RequestLicenseModal
